import React, { memo } from "react";

const LOCK_PATH_PROPS = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2.1,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

const LockGlyph = memo(function LockGlyph() {
  return (
    <svg
      className="preview-mask-chip__lock-svg"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="4" y="11" width="16" height="9" rx="2" {...LOCK_PATH_PROPS} />
      <path d="M8 11V8a4 4 0 1 1 8 0v3" {...LOCK_PATH_PROPS} />
    </svg>
  );
});

function resolveLanguage(language) {
  const normalized = String(language || "").toLowerCase().trim();
  return normalized || "en";
}

function resolveCurrencyCode(currencyCode, currencySymbol) {
  const fromProp = String(currencyCode || "").toUpperCase().trim();
  if (fromProp) return fromProp;
  return currencySymbol === "$" ? "USD" : "EUR";
}

function resolveMaskedText(value, currencySymbol) {
  const fallback = `${currencySymbol} **.**`;
  const raw = typeof value === "string" && value.trim() ? value.trim() : fallback;
  const normalized = raw.replace(/[€$]/g, currencySymbol);

  if (!normalized || /\*\*\._|\*\*_\*|\*\*\.?$/.test(normalized)) {
    return fallback;
  }

  return normalized;
}

function resolveTooltip(language, currencyCode) {
  return language.startsWith("de")
    ? `Vorschau freischalten · Exakte ${currencyCode}-Werte anzeigen`
    : `Unlock preview · See exact ${currencyCode} values`;
}

function MaskedEurCell({
  value,
  currencySymbol = "€",
  currencyCode = "",
  isMasked = true,
  showLock = true,
  className = "",
  dense = false,
  fullWidth = false,
  iconOnly = false,
  language = "",
}) {
  if (!isMasked) {
    return <span className={className}>{value}</span>;
  }

  const resolvedLanguage = resolveLanguage(language);
  const resolvedCurrencyCode = resolveCurrencyCode(currencyCode, currencySymbol);
  const maskedText = resolveMaskedText(value, currencySymbol);
  const tooltipMessage = resolveTooltip(resolvedLanguage, resolvedCurrencyCode);

  // In großen Datentabellen: leichtere Chip-CSS (--table), Tooltip/Hover bleiben über data-preview-tooltip + title.
  const tableOptimized = dense || iconOnly || fullWidth;

  const rootClassName = [
    "preview-mask-cell",
    fullWidth ? "preview-mask-cell--full" : "",
    tableOptimized ? "preview-mask-cell--optimized" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const chipClassName = [
    "preview-mask-chip",
    !tableOptimized ? "preview-mask-chip--interactive" : "",
    tableOptimized ? "preview-mask-chip--table" : "",
    dense ? "preview-mask-chip--dense" : "",
    iconOnly ? "preview-mask-chip--icon-only" : "",
    !showLock ? "preview-mask-chip--no-lock" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const accessibilityProps = {
    "aria-label": tooltipMessage,
    title: tooltipMessage,
    "data-preview-tooltip": tooltipMessage,
  };

  return (
    <span className={rootClassName} {...accessibilityProps}>
      <span className={chipClassName}>
        {showLock ? (
          <span className="preview-mask-chip__icon" aria-hidden="true">
            <LockGlyph />
          </span>
        ) : null}
        {!iconOnly ? (
          <span className="preview-mask-chip__value">{maskedText}</span>
        ) : null}
        {!tableOptimized ? (
          <span className="preview-mask-chip__shine" aria-hidden="true" />
        ) : null}
      </span>
    </span>
  );
}

function areEqual(prevProps, nextProps) {
  return (
    prevProps.value === nextProps.value &&
    prevProps.currencySymbol === nextProps.currencySymbol &&
    prevProps.currencyCode === nextProps.currencyCode &&
    prevProps.isMasked === nextProps.isMasked &&
    prevProps.showLock === nextProps.showLock &&
    prevProps.className === nextProps.className &&
    prevProps.dense === nextProps.dense &&
    prevProps.fullWidth === nextProps.fullWidth &&
    prevProps.iconOnly === nextProps.iconOnly &&
    prevProps.language === nextProps.language
  );
}

export default memo(MaskedEurCell, areEqual);
